import type { RecordingId } from "@repo/app-types/entities/recording";
import type {
  CallOutlineCall,
  CallOutlineSegment,
  TeammateAccountCallOutline,
} from "@repo/app-types/entities/teammateAccountCallOutline";
import { DefaultSkeleton } from "@repo/ui/Skeletons/skeletons";
import { DateTime, Duration } from "luxon";
import { type FC, useEffect, useRef } from "react";
import { twMerge } from "tailwind-merge";
import { renderCallDuration } from "./durationFormatting.js";

export interface CallPosition {
  recordingId: RecordingId;
  timestamp: number;
}

export interface TeammateAccountCallOutlineNavigationProps {
  callOutline: TeammateAccountCallOutline | undefined;
  isLoading?: boolean;
  isError?: boolean;
  callPosition?: CallPosition;
  onCallPositionRequest?: (position: CallPosition) => void;
  shouldAutoScrollToActive?: boolean;
}

export const TeammateAccountCallOutlineNavigation: FC<
  TeammateAccountCallOutlineNavigationProps
> = (props) => {
  const { callOutline } = props;
  const shouldAutoScrollToActive = props.shouldAutoScrollToActive ?? true;
  const recordingId = props.callPosition?.recordingId;
  const timestamp = props.callPosition?.timestamp;

  const checkIsSegmentActive = (
    call: CallOutlineCall,
    segment: CallOutlineSegment,
  ) => {
    return (
      recordingId === call.recording_id &&
      timestamp !== undefined &&
      timestamp >= segment.start_timestamp &&
      timestamp < segment.end_timestamp
    );
  };

  const navRef = useRef<HTMLElement>(null);

  // Scroll the outline to match the current position
  useEffect(() => {
    if (!shouldAutoScrollToActive) {
      return;
    }
    const navElement = navRef.current;
    if (!navElement || recordingId === undefined || timestamp === undefined) {
      return;
    }

    // Find the recording and segment corresponding to the current position
    const recording = callOutline?.calls.find(
      (call) => call.recording_id === recordingId,
    );
    const segment = recording?.segments.find(
      (seg) =>
        seg.start_timestamp <= timestamp && seg.end_timestamp >= timestamp,
    );

    if (!segment) {
      return;
    }

    // Find the element corresponding to that segment
    const segmentElement = navElement.querySelector(
      `[data-recording-id="${recordingId}"][data-timestamp="${segment.start_timestamp}"]`,
    );

    // Scroll the element into view
    if (segmentElement) {
      segmentElement.scrollIntoView({ behavior: "smooth", block: "center" });
    }
  }, [recordingId, timestamp, shouldAutoScrollToActive]);

  if (!callOutline && props.isLoading) {
    return (
      <div className="p-4">
        <DefaultSkeleton />
      </div>
    );
  }

  if (!callOutline && props.isError) {
    return (
      <div>
        <p className="text-sm text-destructive">Failed to load call outline.</p>
      </div>
    );
  }

  return (
    <nav ref={navRef}>
      <ol className="flex flex-col gap-4 text-sm">
        {callOutline?.calls.map((call, callIndex) => (
          <li key={call.recording_id}>
            {/* Call title header */}
            <div className="relative border-gray-100 px-3 py-3">
              <div className="flex items-start justify-between">
                <div className="flex-1">
                  <a
                    href="#"
                    className="text-xs font-medium hover:text-indigo-600"
                    onClick={(e) => {
                      e.preventDefault();

                      if (props.onCallPositionRequest) {
                        props.onCallPositionRequest({
                          recordingId: call.recording_id,
                          timestamp: 0,
                        });
                      }
                    }}
                  >
                    {call.title || `Call ${callIndex + 1}`}
                  </a>
                </div>
              </div>

              <div className="flex items-center gap-1.5 text-xs leading-6 text-gray-600">
                <span>
                  {DateTime.fromISO(call.recorded_at).toLocaleString(
                    DateTime.DATETIME_SHORT,
                  )}
                </span>
                <span className="text-gray-400">•</span>
                <span>{renderCallDuration(call.duration)}</span>
              </div>
            </div>

            {/* Call segments */}
            <div>
              <ol className="flex flex-col">
                {call.segments.map((segment) => {
                  const isActive = checkIsSegmentActive(call, segment);

                  return (
                    <li
                      key={segment.start_timestamp}
                      data-recording-id={call.recording_id}
                      data-timestamp={segment.start_timestamp}
                      className={twMerge(
                        "relative overflow-hidden border-t border-border-light/60 transition-all duration-200",
                        isActive ? "bg-indigo-50/25" : "hover:bg-gray-50/50",
                      )}
                    >
                      <a
                        href="#"
                        className="block px-3 py-1 transition-colors duration-200"
                        onClick={(e) => {
                          e.preventDefault();

                          if (props.onCallPositionRequest) {
                            props.onCallPositionRequest({
                              recordingId: call.recording_id,
                              timestamp: segment.start_timestamp,
                            });
                          }
                        }}
                      >
                        <div className="flex items-center justify-between space-x-2">
                          <h4
                            className={twMerge(
                              "text-xs transition-colors duration-200",
                              isActive
                                ? "text-indigo-900"
                                : " group-hover/segment:text-indigo-600",
                            )}
                          >
                            {segment.title}
                          </h4>
                          <div className="flex items-center gap-1.5 text-xs leading-6">
                            <span
                              className={twMerge(
                                "font-mono",
                                isActive
                                  ? "text-indigo-600"
                                  : "text-muted-foreground",
                              )}
                            >
                              {Duration.fromMillis(
                                segment.start_timestamp,
                              ).toFormat("h:mm:ss")}
                            </span>
                          </div>
                        </div>
                      </a>
                    </li>
                  );
                })}
              </ol>
            </div>
          </li>
        ))}
      </ol>
    </nav>
  );
};
